import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { printerSchema, IPrinter } from './printer';
import { IPrintServer } from '../printServer/printServer';

/**
 * Schema for validating printer form data.
 * Extends the base printer schema with form-specific validations.
 * Replaces ID fields with option type selections.
 *
 * @property {string} name - Name of the printer (max 255 characters)
 * @property {string} [defaultPaper] - Optional default paper type (max 255 characters)
 * @property {string} [apiAddress] - Optional API address for the printer (max 255 characters)
 * @property {OptionType} [printServer] - Print server selection with label/value pair
 */
export const printerFormSchema = printerSchema.extend({
    printServer: optionTypeSchema.refine(val => val !== undefined, { message: 'Print Server is required' }),
}).omit({
    // Omit the original ID field
    printServerId: true,
});

/**
 * Default schema for printer form initialization.
 * Extends the form schema by making required fields optional with defaults.
 *
 * @property {string} [name] - Optional printer name
 * @property {OptionType} [printServer] - Optional print server selection
 */
export const printerDefaultFormSchema = printerFormSchema.extend({
    name: z.string().optional().default(''),
    printServer: optionTypeSchema.optional(),
});

/**
 * TypeScript interface for printer default form values.
 * Generated from the printer default form schema for type safety.
 */
export type IPrinterDefaultFormValues = z.infer<typeof printerDefaultFormSchema>;

/**
 * TypeScript interface representing Printer form values.
 * Generated from the Zod schema for type safety.
 */
export type IPrinterFormValues = z.infer<typeof printerFormSchema>;

/**
 * Converts a Printer model to form values for editing.
 * If no printer is provided, returns default form values.
 * Transforms IDs into option type selections using master data.
 *
 * @param {IPrinter} [printer] - Optional printer model to convert
 * @param {Array<IPrintServer>} [printServers] - Optional array of print servers
 * @returns {IPrinterFormValues | IPrinterDefaultFormValues} Form values ready for editing
 */
export const PrinterFormValues = (
    printer ?: IPrinter,
    printServers ?: Array<IPrintServer>
) : IPrinterFormValues | IPrinterDefaultFormValues => {
    // Handle the default case (new printer)
    if (!printer) {
        return printerDefaultFormSchema.parse({});
    }

    const { printServerId, ...rest } = printer;

    // Transform the IPrinter data into IPrinterFormValues structure
    return printerFormSchema.parse({
        ...rest,
        printServer: printServers?.find(x => x.id === printServerId)
            ? {
                label: printServers.find(x => x.id === printServerId)?.name ?? '',
                value: printServerId,
            }
            : undefined,
    });
};

/**
 * Validates printer form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IPrinterFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validatePrinterForm = (formValues : IPrinterFormValues) : boolean => {
    try {
        printerFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Printer form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
